import CloseIcon from '@mui/icons-material/Close';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PdfPreviewBox from 'app/components/Pdf/PdfPreviewBox';
import { sendOneRecordMailApi } from 'api/notification';
import { getObjectListByPropertyApi } from 'api/general';
import { getOneRecordByIdPDFBlobApi, getReportListByMeetingIdPDFBlobApi } from 'api/pdf';
import { getAllInstUsersApi } from 'api/user';
import {
    getAddDateSuffix,
    getLocaleDateCustom,
    getMeetingTitleByMeeting,
    getMeetingTitleWithMeetingNo,
    getUserFullName,
    mailSmsTextType,
    sortByProperty,
    swal_error,
    swal_success,
    swal_warning
} from 'app/utils/appHelpers';
import { MEETING_USER } from 'app/utils/constants/customConstant';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

const EXTRA_FILE_EXTS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif', '.bmp'];

const MEETING_ACTION_COPY = {
    review: { title: 'Değerlendirme Başlatma', text: 'Değerlendirmeyi başlatmak' },
    vote: { title: 'Oylama Başlatma', text: 'Oylamayı başlatmak' },
    video: { title: 'Video Başlatma', text: 'Video başlatmak' },
    finish: { title: 'Toplantı Güncelleme Tamamlama', text: 'Toplantı güncellemelerinin tamamlanmasını' }
};

const PDF_BOX_SX = {
    border: '1px solid #ddd',
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    bgcolor: '#fafafa'
};

function buildDefaultMailText(meeting, docType, decisionNo) {
    const meetingTitle = getMeetingTitleWithMeetingNo(meeting)?.trim() || 'Toplantı';
    const noText = decisionNo !== undefined && decisionNo !== null && decisionNo !== ''
        ? `${decisionNo} numaralı `
        : '';
    const docLabel = docType === 'agenda' ? 'gündem maddesi' : 'karar';
    return (
        `${meetingTitle}na ilişkin ${noText}${docLabel} ekte gönderilmiştir.\n` +
        `İyi çalışmalar dileriz.`
    );
}

function buildDefaultSubject(meeting, docType, decisionNo) {
    const meetingTitle = getMeetingTitleByMeeting(meeting)?.trim() || 'Toplantı';
    const noText = decisionNo !== undefined && decisionNo !== null && decisionNo !== ''
        ? ` ${decisionNo}`
        : '';
    const docLabel = docType === 'agenda' ? 'Gündem' : 'Karar';
    return `KURULSİS | ${meetingTitle} - ${docLabel}${noText}`;
}

function getVideoMeetingLink(meetingObj) {
    if (!meetingObj) return '';
    const shortUrl = meetingObj.meetingShortUrl;
    if (shortUrl !== null && shortUrl !== undefined && shortUrl !== '') return shortUrl;
    return meetingObj.meetingUrl || '';
}

function getVideoDateInfo(meetingObj) {
    if (!meetingObj?.pdate) return '';
    let saat = new Date(meetingObj.pdate).toTimeString().split(' ')[0].slice(0, 5).replace(':', '.');
    saat = getAddDateSuffix(saat);
    return `${getLocaleDateCustom('tr', meetingObj.pdate)} tarihinde ve saat ${saat} video konferansa açılacaktır.`;
}

function ExtraFilePreviewBox({ file, url, height }) {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isPdf = ext === '.pdf';
    const isImage = ext === '.jpg' || ext === '.jpeg' || ext === '.png' || ext === '.gif' || ext === '.bmp';

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Ek dosya</Typography>
            <Box sx={{ ...PDF_BOX_SX, height }}>
                {isPdf && url ? (
                    <iframe
                        src={url}
                        title="Ek dosya"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                ) : isImage && url ? (
                    <img
                        src={url}
                        alt={file.name}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                ) : (
                    <Typography variant="body2">{file.name}</Typography>
                )}
            </Box>
        </Box>
    );
}

function buildMeetingActionMailText(option, meetingObj, agenda, decision, extraFile) {
    const mailTexts = mailSmsTextType.find((x) => x.type === option);
    if (!mailTexts) return '';

    let mailBodyText = mailTexts.mailBodyText;
    let shortUrlText = '';

    if (option === 'video') {
        mailBodyText += `${getVideoDateInfo(meetingObj)} KURULSİS Ana sayfasındaki video konferans bölümünden veya ekteki toplantı bağlantısından toplantıya katılabilirsiniz.`;
        shortUrlText = ` Toplantı bağlantısı : ${getVideoMeetingLink(meetingObj)}`;
    }

    let mailAttachmentText = '';
    if (agenda && decision) {
        mailAttachmentText = ' Gündem ve karar listesi ekte bulunmaktadır.';
    } else if (agenda) {
        mailAttachmentText = ' Gündem listesi ekte bulunmaktadır.';
    } else if (decision) {
        mailAttachmentText = ' Karar listesi ekte bulunmaktadır.';
    }
    if (extraFile) {
        mailAttachmentText += ' Bu e-postaya ait eklenen dosya ekte bulunmaktadır.';
    }

    return mailBodyText + mailAttachmentText + shortUrlText;
}

function buildMeetingActionSmsText(option, meetingObj) {
    const mailTexts = mailSmsTextType.find((x) => x.type === option);
    if (!mailTexts) return '';

    let smsBodyText = getMeetingTitleByMeeting(meetingObj) + mailTexts.smsBodyText;

    if (option === 'video') {
        const shortUrlText = ` Toplantı bağlantısı : ${getVideoMeetingLink(meetingObj)}`;
        smsBodyText += getVideoDateInfo(meetingObj) + shortUrlText;
    }

    return smsBodyText;
}

function padDatePart(n) {
    return String(n).padStart(2, '0');
}

function buildSendMailSmsInviteBody(meeting) {
    if (!meeting) return '';
    const meetingNo = meeting.meetingNo ?? '';
    const unitName = String(meeting.unit || 'Toplantı').trim();
    const unitTitle = /toplantısı$/i.test(unitName) ? unitName : `${unitName} Toplantısı`;
    let dateStr = '';
    let dayName = '';
    let saat = '';
    if (meeting.pdate) {
        const d = new Date(meeting.pdate);
        dateStr = `${padDatePart(d.getDate())}/${padDatePart(d.getMonth() + 1)}/${d.getFullYear()}`;
        const rawDay = d.toLocaleDateString('tr-TR', { weekday: 'long' });
        dayName = rawDay ? rawDay.charAt(0).toLocaleUpperCase('tr-TR') + rawDay.slice(1) : '';
        saat = getAddDateSuffix(d.toTimeString().split(' ')[0].slice(0, 5).replace(':', '.'));
    }
    return `${meetingNo} sayılı ${unitTitle} ${dateStr} ${dayName} günü saat ${saat} 'Kurulsis' üzerinden yapılacaktır. Bilgilerinize sunulur.`;
}

function getSendMailSmsGreeting(meeting, usePersonalName = false) {
    if (usePersonalName) {
        return 'Sayın [Ad Soyad],';
    }
    return meeting?.inst === 'YK' ? 'Sayın Yönetim Kurulu Üyesi,' : 'Sayın Üye,';
}

function buildSendMailSmsMailText(meeting, agenda, decision, extraFile, usePersonalName = false) {
    let mailBodyText = `${getSendMailSmsGreeting(meeting, usePersonalName)}\n${buildSendMailSmsInviteBody(meeting)}`;
    let mailAttachmentText = '';
    if (agenda && decision) {
        mailAttachmentText = ' Gündem ve karar listesi ekte bulunmaktadır.';
    } else if (agenda) {
        mailAttachmentText = ' Gündem listesi ekte bulunmaktadır.';
    } else if (decision) {
        mailAttachmentText = ' Karar listesi ekte bulunmaktadır.';
    }
    if (extraFile) {
        mailAttachmentText += ' Bu e-postaya ait eklenen dosya ekte bulunmaktadır.';
    }
    return mailBodyText + mailAttachmentText;
}

function buildSendMailSmsSmsText(meeting, usePersonalName = false) {
    return `${getSendMailSmsGreeting(meeting, usePersonalName)}\n${buildSendMailSmsInviteBody(meeting)}`;
}

function getPersonalNameExample(meeting) {
    const body = buildSendMailSmsInviteBody(meeting)
        || "869 sayılı Yönetim Kurulu Toplantısı 28/08/2026 Cuma günü saat 17.31'de 'Kurulsis' üzerinden yapılacaktır. Bilgilerinize sunulur.";
    return `Her kişiye kendi adı yazılır. Örnek:\nSayın Ayşe Yılmaz,\n${body}`;
}

function RecipientGroup({ title, bulkLabel, people, selectedIds, onChange }) {
    const selectedSet = new Set(selectedIds);
    const allSelected = people.length > 0 && people.every((p) => selectedSet.has(p.userId));
    const someSelected = people.some((p) => selectedSet.has(p.userId));

    const toggleAll = (checked) => {
        if (checked) {
            onChange([...new Set([...selectedIds, ...people.map((p) => p.userId)])]);
        } else {
            const remove = new Set(people.map((p) => p.userId));
            onChange(selectedIds.filter((id) => !remove.has(id)));
        }
    };

    const toggleOne = (userId, checked) => {
        if (checked) onChange([...selectedIds, userId]);
        else onChange(selectedIds.filter((id) => id !== userId));
    };

    const compactLabelSx = {
        m: 0,
        ml: 0,
        width: '100%',
        alignItems: 'center',
        '& .MuiCheckbox-root': { p: 0.15, mr: 0.25 },
        '& .MuiFormControlLabel-label': { fontSize: '0.68rem', lineHeight: 1.15 }
    };

    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ mb: 0.1, fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.2 }}>
                {title}
            </Typography>
            {people.length > 0 && (
                <FormControlLabel
                    sx={{ ...compactLabelSx, mb: 0.1 }}
                    control={
                        <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            onChange={(e) => toggleAll(e.target.checked)}
                        />
                    }
                    label={bulkLabel}
                />
            )}
            <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                {people.length === 0 ? (
                    <Typography sx={{ fontSize: '0.68rem', color: '#666', lineHeight: 1.15 }}>Kayıt yok</Typography>
                ) : people.map((p) => {
                    const isAttended = Number(p.attendanceStatus) === 1;
                    return (
                        <FormControlLabel
                            key={p.userId}
                            sx={compactLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedSet.has(p.userId)}
                                    onChange={(e) => toggleOne(p.userId, e.target.checked)}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 0.5, width: '100%' }}>
                                    <span>{getUserFullName(p.user)}</span>
                                    {!isAttended && (
                                        <Typography component="span" sx={{ fontSize: '0.62rem', color: 'error.main', whiteSpace: 'nowrap' }}>
                                            (katılmadı)
                                        </Typography>
                                    )}
                                </Box>
                            }
                        />
                    );
                })}
            </Box>
        </Box>
    );
}

function NotificationDialog({
    open,
    onClose,
    onSuccess,
    meeting,
    decisionId,
    decisionNo: decisionNoProp,
    docType = 'agenda',
    mode = 'oneRecord',
    actionType = 'review',
    onConfirm
}) {
    const isMeetingAction = mode === 'meetingAction';
    const isSendMailSms = mode === 'sendMailSms';
    const showMailSmsForm = isMeetingAction || isSendMailSms;
    const userList = useSelector((state) => state.user.data);

    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [mailText, setMailText] = useState('');
    const [subject, setSubject] = useState('');
    const [sending, setSending] = useState(false);

    const [email, setEmail] = useState(false);
    const [sms, setSms] = useState(false);
    const [agenda, setAgenda] = useState(false);
    const [decision, setDecision] = useState(false);
    const [reminderType, setReminderType] = useState(null);
    const [smsText, setSmsText] = useState('');
    const [mailTextDirty, setMailTextDirty] = useState(false);
    const [smsTextDirty, setSmsTextDirty] = useState(false);
    const [agendaPdfUrl, setAgendaPdfUrl] = useState('');
    const [agendaPdfLoading, setAgendaPdfLoading] = useState(false);
    const [decisionPdfUrl, setDecisionPdfUrl] = useState('');
    const [decisionPdfLoading, setDecisionPdfLoading] = useState(false);
    const [extraFile, setExtraFile] = useState(null);
    const [extraFileUrl, setExtraFileUrl] = useState('');
    const extraFileInputRef = useRef(null);
    const [members, setMembers] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
    const [extraEnabled, setExtraEnabled] = useState(false);
    const [extraSearch, setExtraSearch] = useState('');
    const [extraOptions, setExtraOptions] = useState([]);
    const [extraSelected, setExtraSelected] = useState([]);
    const [extraLoading, setExtraLoading] = useState(false);
    const [usePersonalName, setUsePersonalName] = useState(false);
    const [recipientSearch, setRecipientSearch] = useState('');

    const meetingId = useMemo(() => {
        if (!meeting) return 0;
        return meeting.meetingId !== undefined ? meeting.meetingId : meeting.id;
    }, [meeting]);

    const decisionNo = decisionNoProp ?? meeting?.decisionNo;
    const actionCopy = MEETING_ACTION_COPY[actionType] || MEETING_ACTION_COPY.review;
    const showReminder = actionType === 'vote' || actionType === 'review';
    const showAgendaPdf = showMailSmsForm && agenda;
    const showDecisionPdf = showMailSmsForm && decision;
    const hasMeetingPdf = showAgendaPdf || showDecisionPdf;
    const hasBothMeetingPdfs = showAgendaPdf && showDecisionPdf;
    const showMeetingLeft = showMailSmsForm && (hasMeetingPdf || extraFileUrl);
    const pdfPreviewHeight = { xs: 360, md: 520 };

    const dialogTitle = isSendMailSms
        ? 'E-Posta veya SMS Gönder'
        : isMeetingAction
            ? actionCopy.title
            : (docType === 'agenda' ? 'PDF Tek Gündemi E-Posta Gönder' : 'PDF Tek Kararı E-Posta Gönder');

    useEffect(() => {
        if (!open || showMailSmsForm) return;

        let objectUrl = '';
        let cancelled = false;

        const loadData = async () => {
            setSelectedUsers([]);
            setMailText(buildDefaultMailText(meeting, docType, decisionNo));
            setSubject(buildDefaultSubject(meeting, docType, decisionNo));
            setPdfUrl('');

            setPdfLoading(true);
            setUsersLoading(false);
            setUserOptions([]);
            setRecipientSearch('');

            try {
                const blob = await getOneRecordByIdPDFBlobApi(meetingId, decisionId, docType);
                if (!cancelled) {
                    objectUrl = URL.createObjectURL(blob);
                    setPdfUrl(objectUrl);
                }
            } catch (error) {
                console.error('PDF önizleme hatası:', error);
                if (!cancelled) {
                    swal_error('PDF oluşturulamadı', 2000);
                }
            } finally {
                if (!cancelled) setPdfLoading(false);
            }
        };

        loadData();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, showMailSmsForm, meeting, meetingId, decisionId, decisionNo, docType]);

    useEffect(() => {
        if (!open || showMailSmsForm) return undefined;
        const q = recipientSearch.trim();
        if (q.length < 3) {
            setUserOptions([]);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setUsersLoading(true);
            try {
                const res = await getAllInstUsersApi(q);
                if (cancelled) return;
                const list = Array.isArray(res?.data) ? res.data : [];
                const uniqueById = [];
                const seen = new Set();
                for (const user of list) {
                    if (!user?.id || seen.has(user.id)) continue;
                    seen.add(user.id);
                    uniqueById.push(user);
                }
                uniqueById.sort((a, b) => getUserFullName(a).localeCompare(getUserFullName(b), 'tr'));
                setUserOptions(uniqueById);
            } catch (error) {
                console.error('Kullanıcı arama hatası:', error);
                if (!cancelled) setUserOptions([]);
            } finally {
                if (!cancelled) setUsersLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, showMailSmsForm, recipientSearch]);

    useEffect(() => {
        if (!open || !showMailSmsForm) return;

        setEmail(false);
        setSms(false);
        setAgenda(false);
        setDecision(false);
        setReminderType(null);
        setMailText('');
        setSmsText('');
        setMailTextDirty(false);
        setSmsTextDirty(false);
        setAgendaPdfUrl('');
        setDecisionPdfUrl('');
        setAgendaPdfLoading(false);
        setDecisionPdfLoading(false);
        setExtraFile(null);
        setExtraFileUrl('');
        if (isSendMailSms) {
            setMembers([]);
            setParticipants([]);
            setSelectedMemberIds([]);
            setSelectedParticipantIds([]);
            setExtraEnabled(false);
            setExtraSearch('');
            setExtraOptions([]);
            setExtraSelected([]);
            setUsePersonalName(false);
        }
    }, [open, showMailSmsForm, isSendMailSms, actionType, meetingId]);

    useEffect(() => {
        if (!open || !isSendMailSms) return;

        let cancelled = false;
        const loadUsers = async () => {
            try {
                const res = await getObjectListByPropertyApi(MEETING_USER, 'meetingId', meetingId);
                const list = Array.isArray(res?.data) ? res.data : [];
                const users = Array.isArray(userList) ? userList : [];
                list.forEach((el) => {
                    el.user = users.find((u) => u.id === el.userId);
                });
                const sorted = sortByProperty([...list], 'indexNo') || list;
                const memberList = sorted.filter((x) => Number(x.type) === 1);
                const participantList = sorted.filter((x) => Number(x.type) === 2);
                if (cancelled) return;
                setMembers(memberList);
                setParticipants(participantList);
                setSelectedMemberIds(memberList.filter((m) => Number(m.attendanceStatus) === 1).map((m) => m.userId));
                setSelectedParticipantIds(participantList.filter((p) => Number(p.attendanceStatus) === 1).map((p) => p.userId));
            } catch (error) {
                console.error('Toplantı kişi listesi hatası:', error);
            }
        };
        loadUsers();
        return () => { cancelled = true; };
    }, [open, isSendMailSms, meetingId, userList]);

    useEffect(() => {
        if (!open || !isSendMailSms || !extraEnabled) return undefined;
        const q = extraSearch.trim();
        if (q.length < 3) {
            setExtraOptions([]);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setExtraLoading(true);
            try {
                const res = await getAllInstUsersApi(q);
                if (cancelled) return;
                const list = Array.isArray(res?.data) ? res.data : [];
                const exclude = new Set([
                    ...members.map((m) => m.userId),
                    ...participants.map((p) => p.userId)
                ]);
                setExtraOptions(list.filter((u) => u?.id && !exclude.has(u.id)));
            } catch (error) {
                console.error('Kullanıcı arama hatası:', error);
                if (!cancelled) setExtraOptions([]);
            } finally {
                if (!cancelled) setExtraLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, isSendMailSms, extraEnabled, extraSearch, members, participants]);

    useEffect(() => {
        if (!open || !isMeetingAction || !email || mailTextDirty) return;
        setMailText(buildMeetingActionMailText(actionType, meeting, agenda, decision, extraFile));
    }, [open, isMeetingAction, email, actionType, meeting, agenda, decision, extraFile, mailTextDirty]);

    useEffect(() => {
        if (!open || !isMeetingAction || !sms || smsTextDirty) return;
        setSmsText(buildMeetingActionSmsText(actionType, meeting));
    }, [open, isMeetingAction, sms, actionType, meeting, smsTextDirty]);

    useEffect(() => {
        if (!open || !isSendMailSms || !email || mailTextDirty) return;
        setMailText(buildSendMailSmsMailText(meeting, agenda, decision, extraFile, usePersonalName));
    }, [open, isSendMailSms, email, meeting, agenda, decision, extraFile, mailTextDirty, usePersonalName]);

    useEffect(() => {
        if (!open || !isSendMailSms || !sms || smsTextDirty) return;
        setSmsText(buildSendMailSmsSmsText(meeting, usePersonalName));
    }, [open, isSendMailSms, sms, meeting, smsTextDirty, usePersonalName]);

    useEffect(() => {
        if (!open || !showMailSmsForm || !agenda) {
            return undefined;
        }

        let objectUrl = '';
        let cancelled = false;
        setAgendaPdfLoading(true);
        setAgendaPdfUrl('');

        const loadAgendaPdf = async () => {
            try {
                const blob = await getReportListByMeetingIdPDFBlobApi(meetingId, 'agenda');
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setAgendaPdfUrl(objectUrl);
            } catch (error) {
                console.error('Gündem listesi PDF önizleme hatası:', error);
                if (!cancelled) {
                    swal_error('PDF oluşturulamadı', 2000);
                }
            } finally {
                if (!cancelled) setAgendaPdfLoading(false);
            }
        };

        loadAgendaPdf();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, showMailSmsForm, agenda, meetingId]);

    useEffect(() => {
        if (!open || !showMailSmsForm || !decision) {
            return undefined;
        }

        let objectUrl = '';
        let cancelled = false;
        setDecisionPdfLoading(true);
        setDecisionPdfUrl('');

        const loadDecisionPdf = async () => {
            try {
                const blob = await getReportListByMeetingIdPDFBlobApi(meetingId, 'decision');
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setDecisionPdfUrl(objectUrl);
            } catch (error) {
                console.error('Karar listesi PDF önizleme hatası:', error);
                if (!cancelled) {
                    swal_error('PDF oluşturulamadı', 2000);
                }
            } finally {
                if (!cancelled) setDecisionPdfLoading(false);
            }
        };

        loadDecisionPdf();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, showMailSmsForm, decision, meetingId]);

    const resetMeetingActionForm = () => {
        setEmail(false);
        setSms(false);
        setAgenda(false);
        setDecision(false);
        setReminderType(null);
        setMailText('');
        setSmsText('');
        setMailTextDirty(false);
        setSmsTextDirty(false);
        setAgendaPdfLoading(false);
        setDecisionPdfLoading(false);
        if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
        setExtraFile(null);
        setExtraFileUrl('');
        setMembers([]);
        setParticipants([]);
        setSelectedMemberIds([]);
        setSelectedParticipantIds([]);
        setExtraEnabled(false);
        setExtraSearch('');
        setExtraOptions([]);
        setExtraSelected([]);
        setUsePersonalName(false);
    };

    const closeDialog = () => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        if (agendaPdfUrl) URL.revokeObjectURL(agendaPdfUrl);
        if (decisionPdfUrl) URL.revokeObjectURL(decisionPdfUrl);
        if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
        setPdfUrl('');
        setAgendaPdfUrl('');
        setDecisionPdfUrl('');
        setExtraFileUrl('');
        if (showMailSmsForm) resetMeetingActionForm();
        onClose();
    };

    const handleClose = () => {
        if (sending) return;
        closeDialog();
    };

    const handleEmailChange = (checked) => {
        setEmail(checked);
        if (!checked) {
            setAgenda(false);
            setDecision(false);
            setMailTextDirty(false);
            if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
            setExtraFile(null);
            setExtraFileUrl('');
            if (!sms) setReminderType(null);
        }
    };

    const handleSmsChange = (checked) => {
        setSms(checked);
        if (!checked) {
            setSmsTextDirty(false);
            if (!email) setReminderType(null);
        }
    };

    const replaceGreetingLine = (text, greeting) => {
        const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
        if (!text || !String(text).trim()) {
            return greeting;
        }
        if (lines.length > 0 && /^Sayın\s+/i.test(lines[0].trim())) {
            lines[0] = greeting;
            return lines.join('\n');
        }
        return `${greeting}\n${text}`;
    };

    const handlePersonalNameChange = (checked) => {
        setUsePersonalName(checked);
        const greeting = getSendMailSmsGreeting(meeting, checked);
        if (email) {
            setMailText((prev) => replaceGreetingLine(prev, greeting));
        }
        if (sms) {
            setSmsText((prev) => replaceGreetingLine(prev, greeting));
        }
    };

    const handleReminderChange = (type, checked) => {
        setReminderType(checked ? type : null);
    };

    const handleExtraFileChange = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!EXTRA_FILE_EXTS.includes(ext)) {
            swal_warning('PDF, Word, Excel veya resim dosyası seçiniz', 2000);
            return;
        }
        if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
        setExtraFile(file);
        setExtraFileUrl(URL.createObjectURL(file));
    };

    const handleExtraFileRemove = () => {
        if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
        setExtraFile(null);
        setExtraFileUrl('');
    };

    const handleSend = async () => {
        if (selectedUsers.length === 0) {
            swal_warning('En az bir alıcı seçiniz', 2000);
            return;
        }
        if (!mailText.trim()) {
            swal_warning('E-posta metni boş olamaz', 2000);
            return;
        }

        setSending(true);
        Swal.fire({
            title: 'İşleminiz gerçekleştiriliyor...',
            html: '<div><CircularProgress /></div>',
            allowOutsideClick: false,
            showConfirmButton: false,
            customClass: {
                container: 'swal-container-custom'
            },
            didOpen: () => Swal.showLoading()
        });

        try {
            const result = await sendOneRecordMailApi({
                meetingId,
                decisionId,
                type: docType,
                userIds: selectedUsers.map((u) => u.id),
                mailText: mailText.trim(),
                subject: subject.trim()
            });

            await Swal.close();
            setSending(false);

            if (result?.status) {
                const sentCount = result.sentCount || selectedUsers.length;
                closeDialog();
                if (onSuccess) {
                    onSuccess(sentCount);
                } else {
                    swal_success(`${sentCount} kişiye e-posta gönderildi`, 3000);
                }
            } else {
                swal_error(result?.message || 'E-posta gönderilemedi', 3000);
            }
        } catch (error) {
            await Swal.close();
            setSending(false);
            swal_error('E-posta gönderilemedi', 3000);
        }
    };

    const handleMeetingActionConfirm = () => {
        if (onConfirm) {
            onConfirm({
                email,
                sms,
                agenda: email && agenda,
                decision: email && decision,
                reminderType: (email || sms) ? reminderType : null,
                mailText: email ? mailText : null,
                smsText: sms ? smsText : null,
                extraFile: email ? extraFile : null
            });
        }
        closeDialog();
    };

    const handleSendMailSmsConfirm = () => {
        if (!email && !sms) {
            swal_warning('E-posta veya SMS seçiniz', 2000);
            return;
        }
        const userIds = [...new Set([
            ...selectedMemberIds,
            ...selectedParticipantIds,
            ...extraSelected.map((u) => u.id)
        ].filter(Boolean))];
        if (userIds.length === 0) {
            swal_warning('En az bir alıcı seçiniz', 2000);
            return;
        }
        if (email && !mailText.trim()) {
            swal_warning('E-posta metni boş olamaz', 2000);
            return;
        }
        if (sms && !smsText.trim()) {
            swal_warning('SMS metni boş olamaz', 2000);
            return;
        }
        if (onConfirm) {
            onConfirm({
                email,
                sms,
                agenda: email && agenda,
                decision: email && decision,
                mailText: email ? mailText : null,
                smsText: sms ? smsText : null,
                extraFile: email ? extraFile : null,
                userIds,
                personalizeByName: usePersonalName
            });
        }
        closeDialog();
    };

    const confirmLabel = isSendMailSms
        ? 'Gönder'
        : isMeetingAction
            ? ((email || sms) ? 'Gönder' : 'Evet')
            : 'Gönder';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={(!showMailSmsForm || showMeetingLeft) ? 'lg' : 'md'}
            fullWidth
        >
            <DialogTitle sx={{ pr: 6 }}>
                {dialogTitle}
                <IconButton
                    aria-label="kapat"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    {!showMailSmsForm && (
                        <Grid item xs={12} md={6}>
                            <PdfPreviewBox
                                title="PDF Önizleme"
                                loading={pdfLoading}
                                url={pdfUrl}
                                height={pdfPreviewHeight}
                            />
                        </Grid>
                    )}

                    {showMailSmsForm && showMeetingLeft && (
                        <Grid item xs={12} md={6}>
                            {hasBothMeetingPdfs ? (
                                <Box sx={{
                                    height: pdfPreviewHeight,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1
                                }}>
                                    <PdfPreviewBox
                                        flexGrow
                                        title="Gündem listesi"
                                        loading={agendaPdfLoading}
                                        url={agendaPdfUrl}
                                    />
                                    <PdfPreviewBox
                                        flexGrow
                                        title="Karar listesi"
                                        loading={decisionPdfLoading}
                                        url={decisionPdfUrl}
                                    />
                                </Box>
                            ) : (
                                <>
                                    {showAgendaPdf && (
                                        <PdfPreviewBox
                                            title="Gündem listesi"
                                            loading={agendaPdfLoading}
                                            url={agendaPdfUrl}
                                            height={pdfPreviewHeight}
                                        />
                                    )}
                                    {showDecisionPdf && (
                                        <PdfPreviewBox
                                            title="Karar listesi"
                                            loading={decisionPdfLoading}
                                            url={decisionPdfUrl}
                                            height={pdfPreviewHeight}
                                        />
                                    )}
                                </>
                            )}
                            {extraFile && extraFileUrl && (
                                <ExtraFilePreviewBox
                                    file={extraFile}
                                    url={extraFileUrl}
                                    height={pdfPreviewHeight}
                                />
                            )}
                        </Grid>
                    )}

                    <Grid item xs={12} md={(!showMailSmsForm || showMeetingLeft) ? 6 : 12}>
                        {!isSendMailSms && (
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {meeting ? getMeetingTitleByMeeting(meeting) : ''}
                            </Typography>
                        )}

                        {showMailSmsForm ? (
                            <>
                                {isSendMailSms && (
                                    <>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <RecipientGroup
                                                    title="Üyeler"
                                                    bulkLabel="Üyeleri toplu seç"
                                                    people={members}
                                                    selectedIds={selectedMemberIds}
                                                    onChange={setSelectedMemberIds}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <RecipientGroup
                                                    title="Katılımcılar"
                                                    bulkLabel="Katılımcıları toplu seç"
                                                    people={participants}
                                                    selectedIds={selectedParticipantIds}
                                                    onChange={setSelectedParticipantIds}
                                                />
                                            </Box>
                                        </Box>
                                        <FormControlLabel
                                            sx={{ mb: extraEnabled ? 0.25 : 0.75, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' }, '& .MuiCheckbox-root': { p: 0.25 } }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={extraEnabled}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setExtraEnabled(checked);
                                                        if (!checked) {
                                                            setExtraSelected([]);
                                                            setExtraSearch('');
                                                            setExtraOptions([]);
                                                        }
                                                    }}
                                                />
                                            }
                                            label="Farklı bir kişi"
                                        />
                                        {extraEnabled && (
                                            <Autocomplete
                                                multiple
                                                options={extraOptions}
                                                loading={extraLoading}
                                                value={extraSelected}
                                                onChange={(_, value) => setExtraSelected(value)}
                                                inputValue={extraSearch}
                                                onInputChange={(_, value) => setExtraSearch(value)}
                                                getOptionLabel={(option) => getUserFullName(option)}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                filterSelectedOptions
                                                filterOptions={(opts) => opts}
                                                noOptionsText={extraSearch.trim().length < 3 ? 'En az 3 harf yazınız' : 'Kayıt bulunamadı'}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Kişi ara"
                                                        placeholder="Ad veya soyad"
                                                        size="small"
                                                        margin="dense"
                                                    />
                                                )}
                                                sx={{ mb: 2 }}
                                            />
                                        )}
                                    </>
                                )}

                                <Typography
                                    variant="body2"
                                    sx={{ fontStyle: 'italic', color: '#666', mb: isSendMailSms ? 0.75 : 2, fontSize: '0.7rem' }}
                                >
                                    {isSendMailSms
                                        ? 'E-posta veya SMS seçiniz. E-posta seçtiyseniz gündem ve/veya karar listesini ekleyebilirsiniz. Seçilen kişilere gönderilir.'
                                        : 'E-posta veya SMS seçerseniz ilgili kişilere bildirim gönderilir. E-posta seçtiyseniz gündem ve/veya karar listesini ekleyebilirsiniz. Hiçbirini seçmezseniz bildirim gitmeden işlem başlar.'}
                                </Typography>

                                <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                                    Bildirim türünü seçiniz:
                                </Typography>
                                <FormGroup row sx={{ mb: 2, gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={email}
                                                onChange={(e) => handleEmailChange(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label="E‑posta"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={sms}
                                                onChange={(e) => handleSmsChange(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label="SMS"
                                    />
                                    {isSendMailSms && (
                                        <Tooltip
                                            title={<span style={{ whiteSpace: 'pre-line' }}>{getPersonalNameExample(meeting)}</span>}
                                            placement="top"
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={usePersonalName}
                                                        onChange={(e) => handlePersonalNameChange(e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label="Ad soyad ile gönderilsin"
                                            />
                                        </Tooltip>
                                    )}
                                </FormGroup>

                                <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                                    E‑posta (seçtiyseniz) ekini seçiniz:
                                </Typography>
                                <FormGroup row sx={{ mb: extraFile ? 0.5 : 2, gap: 2, alignItems: 'center' }}>
                                    <FormControlLabel
                                        disabled={!email}
                                        control={
                                            <Checkbox
                                                checked={agenda}
                                                onChange={(e) => setAgenda(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label="Gündem Listesi"
                                    />
                                    <FormControlLabel
                                        disabled={!email}
                                        control={
                                            <Checkbox
                                                checked={decision}
                                                onChange={(e) => setDecision(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label="Karar Listesi"
                                    />
                                    <input
                                        ref={extraFileInputRef}
                                        type="file"
                                        hidden
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp"
                                        onChange={handleExtraFileChange}
                                    />
                                    <Button
                                        variant="outlined"
                                        disabled={!email}
                                        onClick={() => extraFileInputRef.current && extraFileInputRef.current.click()}
                                        sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4, textTransform: 'none' }}
                                    >
                                        Dosya ekle
                                    </Button>
                                </FormGroup>
                                {extraFile && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{extraFile.name}</Typography>
                                        <Button
                                            onClick={handleExtraFileRemove}
                                            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4, textTransform: 'none' }}
                                        >
                                            Kaldır
                                        </Button>
                                    </Box>
                                )}

                                {isMeetingAction && showReminder && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                                            Hatırlatma seçeneği (Sms ve E-posta olarak):
                                        </Typography>
                                        <FormGroup sx={{ mb: 2 }}>
                                            <FormControlLabel
                                                disabled={!email && !sms}
                                                control={
                                                    <Checkbox
                                                        checked={reminderType === 1}
                                                        onChange={(e) => handleReminderChange(1, e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label="Sadece bir kez (24 saat sonra)"
                                            />
                                            <FormControlLabel
                                                disabled={!email && !sms}
                                                control={
                                                    <Checkbox
                                                        checked={reminderType === 2}
                                                        onChange={(e) => handleReminderChange(2, e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label="Oylama sonlanana kadar her 24 saatte bir"
                                            />
                                        </FormGroup>
                                    </>
                                )}

                                {email && (
                                    <>
                                        {!isSendMailSms && (
                                            <Typography variant="body2" sx={{ mb: 1, fontSize: '0.7rem' }}>
                                                E-posta başlığı : {meeting ? getMeetingTitleByMeeting(meeting) : ''}
                                            </Typography>
                                        )}
                                        <TextField
                                            label="E-posta Metni"
                                            value={mailText}
                                            onChange={(e) => {
                                                setMailTextDirty(true);
                                                setMailText(e.target.value);
                                            }}
                                            fullWidth
                                            multiline
                                            minRows={6}
                                            margin="dense"
                                            sx={{ mb: 1 }}
                                        />
                                    </>
                                )}

                                {sms && (
                                    <TextField
                                        label="SMS Metni"
                                        value={smsText}
                                        onChange={(e) => {
                                            setSmsTextDirty(true);
                                            setSmsText(e.target.value);
                                        }}
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        margin="dense"
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Autocomplete
                                    multiple
                                    options={userOptions}
                                    loading={usersLoading}
                                    value={selectedUsers}
                                    onChange={(_, value) => setSelectedUsers(value)}
                                    inputValue={recipientSearch}
                                    onInputChange={(_, value) => setRecipientSearch(value)}
                                    getOptionLabel={(option) => getUserFullName(option)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    filterSelectedOptions
                                    filterOptions={(opts) => opts}
                                    noOptionsText={recipientSearch.trim().length < 3 ? 'En az 3 harf yazınız' : 'Kayıt bulunamadı'}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Alıcılar"
                                            placeholder="Üye seçiniz"
                                            size="small"
                                            margin="dense"
                                        />
                                    )}
                                    sx={{ mb: 1 }}
                                />

                                <TextField
                                    label="Konu"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                    sx={{ mb: 1 }}
                                />

                                <TextField
                                    label="E-posta metni"
                                    value={mailText}
                                    onChange={(e) => setMailText(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={10}
                                    margin="dense"
                                    helperText="[Ad Soyad] seçtiğiniz her kişi için kendi ad ve soyadlarıyla otomatik olarak değiştirilir. Lütfen [Ad Soyad] kısmında değişiklik yapmayınız."
                                />
                            </>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={sending}>Vazgeç</Button>
                <Button
                    variant="contained"
                    onClick={isSendMailSms ? handleSendMailSmsConfirm : (isMeetingAction ? handleMeetingActionConfirm : handleSend)}
                    disabled={(isMeetingAction || isSendMailSms) ? false : (sending || selectedUsers.length === 0 || !mailText.trim())}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NotificationDialog;
